/**
 * Gobodo-backed email template service.
 * CRUD for EmailTemplate__c records when REACT_APP_USE_GOBODO_API=true.
 */

import { getCustomSuffix } from '../../config/gobodoConfig';
import { shouldUseGobodo } from '../../utils/gobodoUtils';
import { ensureObjectTypeExists } from '../../utils/gobodoObjectTypeVerifier';
import {
  getGobodoRecords,
  getGobodoRecordById,
  createGobodoRecord,
  updateGobodoRecord,
  deleteGobodoRecord,
} from './gobodoRecordService';
import type { GenericRecord } from '../recordStorageService';
import type {
  EmailTemplate,
  EmailTemplateDepartment,
  EmailTemplateStatus,
  CreateTemplateRequest,
  UpdateTemplateRequest,
  TemplateVariable,
} from '../../types/emailTemplates';

const getEmailTemplateObjectKey = (): string =>
  `EmailTemplate${getCustomSuffix()}`;

function toIsoString(val: unknown): string {
  if (val == null) return new Date().toISOString();
  if (typeof val === 'string') return val;
  if (typeof val === 'object' && val !== null) {
    const obj = val as Record<string, unknown>;
    if ('toISOString' in obj && typeof obj.toISOString === 'function') {
      return (obj.toISOString as () => string)();
    }
  }
  return new Date().toISOString();
}

function parseJson<T>(val: unknown, fallback: T): T {
  if (val == null) return fallback;
  if (typeof val === 'string' && val.trim()) {
    try {
      return JSON.parse(val) as T;
    } catch {
      return fallback;
    }
  }
  if (Array.isArray(val)) return val as T;
  if (typeof val === 'object') return val as T;
  return fallback;
}

function recordToEmailTemplate(record: GenericRecord): EmailTemplate {
  const r = record as Record<string, unknown>;
  const raw = (r.Name__c !== undefined ? r : record) as Record<string, unknown>;
  const variables = parseJson<TemplateVariable[]>(raw.VariablesJson__c, []);
  const defaultValues = parseJson<Record<string, string>>(raw.DefaultValuesJson__c, {});
  const department = (raw.Department__c as string) || 'all';
  const status = (raw.Status__c as string) || 'draft';
  const isCurrent = String(raw.IsCurrentVersion__c).toLowerCase() === 'true';

  const result: EmailTemplate = {
    id: String(record.id),
    templateKey: String(raw.TemplateKey__c ?? raw.Name__c ?? ''),
    name: String(raw.Name__c ?? ''),
    description: raw.Description__c != null ? String(raw.Description__c) : undefined,
    subject: String(raw.Subject__c ?? ''),
    bodyHtml: String(raw.BodyHtml__c ?? ''),
    bodyText: raw.BodyText__c != null ? String(raw.BodyText__c) : undefined,
    department: department as EmailTemplateDepartment,
    status: status as EmailTemplateStatus,
    isSystemTemplate: String(raw.IsSystemTemplate__c).toLowerCase() === 'true',
    versionNumber: Number(raw.VersionNumber__c) || 1,
    isCurrentVersion: isCurrent,
    parentVersionId: raw.ParentVersionId__c != null ? String(raw.ParentVersionId__c) : undefined,
    usageCount: Number(raw.UsageCount__c) || 0,
    lastUsedAt: raw.LastUsedAt__c != null ? toIsoString(raw.LastUsedAt__c) : undefined,
    hasEmbeddedImages: false,
    embeddedImages: [],
    variables,
    defaultValues,
    createdBy: String(raw.CreatedBy__c ?? record.createdBy ?? ''),
    createdAt: toIsoString(raw.CreatedAt__c ?? record.createdDate),
    updatedBy: raw.UpdatedBy__c != null ? String(raw.UpdatedBy__c) : undefined,
    updatedAt: toIsoString(raw.UpdatedAt__c ?? record.lastModified),
  };
  (result as unknown as Record<string, unknown>).category = String(raw.Category__c ?? 'custom');
  return result;
}

function emailTemplateToRecord(template: Partial<EmailTemplate>): Record<string, unknown> {
  const t = template as Record<string, unknown>;
  const props: Record<string, unknown> = {};
  if (t.name !== undefined) props.Name__c = t.name;
  if (t.templateKey !== undefined) props.TemplateKey__c = t.templateKey;
  if (t.description !== undefined) props.Description__c = t.description;
  if (t.subject !== undefined) props.Subject__c = t.subject;
  if (t.bodyHtml !== undefined) props.BodyHtml__c = t.bodyHtml;
  if (t.bodyText !== undefined) props.BodyText__c = t.bodyText;
  if (t.department !== undefined) props.Department__c = t.department;
  if (t.status !== undefined) props.Status__c = t.status;
  if (t.isSystemTemplate !== undefined) props.IsSystemTemplate__c = t.isSystemTemplate ? 'true' : 'false';
  if (t.usageCount !== undefined) props.UsageCount__c = t.usageCount;
  if (t.lastUsedAt !== undefined) props.LastUsedAt__c = t.lastUsedAt;
  if (t.createdBy !== undefined) props.CreatedBy__c = t.createdBy;
  if (t.createdAt !== undefined) props.CreatedAt__c = t.createdAt;
  if (t.updatedBy !== undefined) props.UpdatedBy__c = t.updatedBy;
  if (t.updatedAt !== undefined) props.UpdatedAt__c = t.updatedAt;
  if (t.versionNumber !== undefined) props.VersionNumber__c = t.versionNumber;
  if (t.isCurrentVersion !== undefined) props.IsCurrentVersion__c = t.isCurrentVersion ? 'true' : 'false';
  if (t.parentVersionId !== undefined) props.ParentVersionId__c = t.parentVersionId;
  if (t.variables !== undefined) props.VariablesJson__c = JSON.stringify(t.variables);
  if (t.defaultValues !== undefined) props.DefaultValuesJson__c = JSON.stringify(t.defaultValues);
  const cat = (t as Record<string, unknown>).category;
  if (cat !== undefined && typeof cat === 'string') props.Category__c = cat;
  return props;
}

export interface ListEmailTemplatesOptions {
  onlyCurrentVersions?: boolean;
  page?: number;
  size?: number;
}

export async function listEmailTemplates(
  options?: ListEmailTemplatesOptions
): Promise<EmailTemplate[]> {
  if (!shouldUseGobodo()) return [];

  const key = getEmailTemplateObjectKey();
  const page = options?.page ?? 0;
  const size = options?.size ?? 200;
  const { records } = await getGobodoRecords(key, { page, size });
  if (records.length === 0 && typeof process !== 'undefined' && process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
    console.warn(
      '[gobodoEmailTemplateService] No email templates from Gobodo. Ensure (1) REACT_APP_USE_GOBODO_API=true, (2) REACT_APP_GOBODO_GRAPH_ID and REACT_APP_GOBODO_CUSTOM_SUFFIX match the env used when you ran setup:gobodo-email-template and seed:gobodo-email-templates.'
    );
  }
  let list = records.map(recordToEmailTemplate);
  if (options?.onlyCurrentVersions) {
    list = list.filter((t) => t.isCurrentVersion);
  }
  return list.sort((a, b) => a.name.localeCompare(b.name));
}

export async function getEmailTemplateById(id: string): Promise<EmailTemplate | null> {
  if (!shouldUseGobodo()) return null;

  const record = await getGobodoRecordById(id);
  if (!record) return null;
  return recordToEmailTemplate(record);
}

export async function createEmailTemplate(
  data: CreateTemplateRequest & { category?: string },
  userId: string
): Promise<EmailTemplate> {
  if (!shouldUseGobodo()) {
    throw new Error('Gobodo API not enabled. Set REACT_APP_USE_GOBODO_API=true');
  }

  const key = getEmailTemplateObjectKey();
  await ensureObjectTypeExists(key, {
    label: 'Email Template',
    pluralLabel: 'Email Templates',
    description: 'Email template for composition and send',
    category: 'core',
  });

  const now = new Date().toISOString();
  const templateKey = data.templateKey || data.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 100);
  const category = (data as unknown as Record<string, unknown>).category as string | undefined;
  const recordData: Record<string, unknown> = {
    ...emailTemplateToRecord({
      name: data.name,
      templateKey,
      description: data.description,
      subject: data.subject,
      bodyHtml: data.bodyHtml,
      bodyText: data.bodyText,
      department: data.department,
      status: data.status || 'draft',
      isSystemTemplate: false,
      versionNumber: 1,
      isCurrentVersion: true,
      variables: data.variables || [],
      defaultValues: data.defaultValues || {},
      createdBy: userId,
      createdAt: now,
      updatedBy: userId,
      updatedAt: now,
    }),
  };
  if (category) recordData.Category__c = category;
  recordData.CreatedBy__c = userId;
  recordData.CreatedAt__c = now;
  recordData.UpdatedBy__c = userId;
  recordData.UpdatedAt__c = now;
  recordData.VersionNumber__c = 1;
  recordData.IsCurrentVersion__c = 'true';

  const created = await createGobodoRecord(key, recordData as Record<string, any>);
  return recordToEmailTemplate(created);
}

export async function updateEmailTemplate(
  id: string,
  data: UpdateTemplateRequest,
  userId: string
): Promise<EmailTemplate> {
  if (!shouldUseGobodo()) {
    throw new Error('Gobodo API not enabled. Set REACT_APP_USE_GOBODO_API=true');
  }

  if (data.createNewVersion) {
    return createNewVersion(id, data, userId);
  }

  const now = new Date().toISOString();
  const updates: Record<string, unknown> = {
    ...emailTemplateToRecord(data),
    UpdatedBy__c: userId,
    UpdatedAt__c: now,
  };
  const updated = await updateGobodoRecord(id, updates as Record<string, any>);
  return recordToEmailTemplate(updated);
}

export async function createNewVersion(
  templateId: string,
  changes: UpdateTemplateRequest,
  userId: string
): Promise<EmailTemplate> {
  if (!shouldUseGobodo()) {
    throw new Error('Gobodo API not enabled. Set REACT_APP_USE_GOBODO_API=true');
  }

  const current = await getEmailTemplateById(templateId);
  if (!current) throw new Error('Template not found');

  const key = getEmailTemplateObjectKey();
  const now = new Date().toISOString();
  const newVersionNumber = current.versionNumber + 1;
  const recordData: Record<string, unknown> = {
    Name__c: changes.name ?? current.name,
    TemplateKey__c: current.templateKey,
    Subject__c: changes.subject ?? current.subject,
    BodyHtml__c: changes.bodyHtml ?? current.bodyHtml,
    BodyText__c: changes.bodyText ?? current.bodyText,
    Department__c: current.department,
    Status__c: changes.status ?? current.status,
    IsSystemTemplate__c: current.isSystemTemplate ? 'true' : 'false',
    VersionNumber__c: newVersionNumber,
    IsCurrentVersion__c: 'true',
    ParentVersionId__c: templateId,
    VariablesJson__c: JSON.stringify(changes.variables ?? current.variables),
    DefaultValuesJson__c: JSON.stringify(changes.defaultValues ?? current.defaultValues),
    CreatedBy__c: userId,
    CreatedAt__c: now,
    UpdatedBy__c: userId,
    UpdatedAt__c: now,
    UsageCount__c: 0,
  };

  const created = await createGobodoRecord(key, recordData as Record<string, any>);
  await updateGobodoRecord(templateId, { IsCurrentVersion__c: 'false' } as Record<string, any>, {
    gobodoUpdateMode: 'propertyPatch',
  });
  return recordToEmailTemplate(created);
}

export async function getTemplateVersions(templateKey: string): Promise<EmailTemplate[]> {
  if (!shouldUseGobodo()) return [];

  const all = await listEmailTemplates({ onlyCurrentVersions: false });
  const versions = all.filter((t) => t.templateKey === templateKey);
  return versions.sort((a, b) => a.versionNumber - b.versionNumber);
}

export async function deleteEmailTemplate(id: string): Promise<boolean> {
  if (!shouldUseGobodo()) {
    throw new Error('Gobodo API not enabled. Set REACT_APP_USE_GOBODO_API=true');
  }
  await deleteGobodoRecord(id);
  return true;
}
